import * as Sentry from "@sentry/react";

export interface ErrorLogEntry {
  message: string;
  stack?: string;
  module?: string;
  userRole?: string;
  route?: string;
  timestamp: string;
  severity: 'error' | 'warning' | 'info';
  context?: Record<string, unknown>;
}

const errorLog: ErrorLogEntry[] = [];
const MAX_LOG_SIZE = 200;

export function logError(entry: Omit<ErrorLogEntry, 'timestamp'> & { timestamp?: string }): ErrorLogEntry {
  const full: ErrorLogEntry = {
    ...entry,
    timestamp: entry.timestamp ?? new Date().toISOString(),
  };

  errorLog.push(full);
  if (errorLog.length > MAX_LOG_SIZE) errorLog.shift();

  if (import.meta.env.DEV) {
    console.error(`[ErrorLogger] ${full.severity.toUpperCase()} — ${full.module ?? 'unknown'}:`, full.message, full.stack ?? '');
  }

  // Forward to Sentry
  Sentry.withScope((scope) => {
    scope.setTag("module", full.module ?? "unknown");
    scope.setTag("severity", full.severity);
    if (full.userRole) scope.setTag("userRole", full.userRole);
    if (full.route) scope.setTag("route", full.route);
    if (full.context) scope.setContext("extra", full.context);

    if (full.severity === "error") {
      Sentry.captureException(new Error(full.message));
    } else {
      Sentry.captureMessage(full.message, full.severity === "warning" ? "warning" : "info");
    }
  });

  return full;
}

export function getErrorLog(): ReadonlyArray<ErrorLogEntry> {
  return errorLog;
}

export function clearErrorLog(): void {
  errorLog.length = 0;
}

/**
 * Wrap an async action with standardized error handling.
 * Returns { success, data?, error? }
 */
export async function safeAsync<T>(
  fn: () => Promise<T>,
  opts?: { module?: string; userRole?: string; route?: string }
): Promise<{ success: true; data: T } | { success: false; error: string }> {
  try {
    const data = await fn();
    return { success: true, data };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    const stack = err instanceof Error ? err.stack : undefined;
    logError({
      message,
      stack,
      severity: 'error',
      ...opts,
    });
    return { success: false, error: message };
  }
}

/**
 * Wrap a sync action that might throw.
 */
export function safeSyncAction<T>(
  fn: () => T,
  opts?: { module?: string; userRole?: string; route?: string }
): { success: true; data: T } | { success: false; error: string } {
  try {
    const data = fn();
    return { success: true, data };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    const stack = err instanceof Error ? err.stack : undefined;
    logError({
      message,
      stack,
      severity: 'error',
      ...opts,
    });
    return { success: false, error: message };
  }
}
